import React, { useState } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { BookOpen } from 'lucide-react'
import { Badge } from '@/components/ui/badge'

export function BooksPanel() {
  const [status, setStatus] = useState<string>('all')
  const [filter, setFilter] = useState<string>('')
  const [date, setDate] = useState<string>('')
  const [pageNumber, setPageNumber] = useState<string>('')

  const handlePageNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target

    if (value === '' || (/^\d+$/.test(value) && parseInt(value, 10) >= 1)) {
      setPageNumber(value)
    }
  }

  const filterOptions = [
    { value: 'all', label: 'All' },
    { value: 'recently-edited', label: 'Recently edited' },
    { value: 'currently-open', label: 'Currently open' },
    { value: 'hi-res-pdfs-ready', label: 'Hi-Res PDFs Ready' },
    { value: 'overflow-text', label: 'Overflow text' },
    { value: 'index-pages', label: 'Index pages' },
    { value: 'portrait-flow-one', label: 'Portrait flow one' },
    { value: 'portrait-flow-two', label: 'Portrait flow two' },
  ]

  const dateOptions = [
    { value: '8/7/2025', label: '8/7/2025' },
    { value: '5/4/2025', label: '5/4/2025' },
    { value: '5/3/2025', label: '5/3/2025' },
    { value: '10/1/2025', label: '10/1/2025' },
    { value: '12/1/2025', label: '12/1/2025' },
    { value: '9/9/25', label: '9/9/25' },
  ]

  return (
    <>
      <div className="right-panel h-px border-b border-border m-0 p-0 -ms-9 -me-2 mb-4" />
      <div className="h-full max-h-[70vh] overflow-y-auto">
        <div className="flex flex-col gap-3 pl-1">
          <ToggleGroup
            type="single"
            value={status}
            onValueChange={(v) => v && setStatus(v)}
            className="justify-start gap-1 flex-nowrap overflow-x-auto"
          >
            <ToggleGroupItem
              value="all"
              aria-label="All"
              className="h-auto px-2 py-[10px] text-xs font-medium rounded-md bg-transparent shrink-0 data-[state=on]:bg-secondary data-[state=on]:border-background data-[state=on]:text-foreground"
            >
              All
            </ToggleGroupItem>
            <ToggleGroupItem
              value="blank"
              aria-label="Blank"
              className="h-auto px-2 py-[10px] text-xs font-medium rounded-md bg-transparent shrink-0 data-[state=on]:bg-secondary data-[state=on]:border-background data-[state=on]:text-foreground"
            >
              Blank
            </ToggleGroupItem>
            <ToggleGroupItem
              value="started"
              aria-label="Started"
              className="h-auto px-2 py-[10px] text-xs font-medium rounded-md bg-transparent shrink-0 data-[state=on]:bg-secondary data-[state=on]:border-background data-[state=on]:text-foreground"
            >
              Started
            </ToggleGroupItem>
            <ToggleGroupItem
              value="proof"
              aria-label="Proof"
              className="h-auto px-2 py-[10px] text-xs font-medium rounded-md bg-transparent shrink-0 data-[state=on]:bg-secondary data-[state=on]:border-background data-[state=on]:text-foreground"
            >
              Proof
            </ToggleGroupItem>
            <ToggleGroupItem
              value="approved"
              aria-label="Approved"
              className="h-auto px-2 py-[10px] text-xs font-medium rounded-md bg-transparent shrink-0 data-[state=on]:bg-secondary data-[state=on]:border-background data-[state=on]:text-foreground"
            >
              Approved
            </ToggleGroupItem>
            <ToggleGroupItem
              value="submitted"
              aria-label="Submitted"
              className="h-auto px-2 py-[10px] text-xs font-medium rounded-md bg-transparent shrink-0 data-[state=on]:bg-secondary data-[state=on]:border-background data-[state=on]:text-foreground"
            >
              Submitted
            </ToggleGroupItem>
          </ToggleGroup>
          <div className="w-full">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-full h-9 bg-card">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                {filterOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <Select value={date} onValueChange={setDate}>
                <SelectTrigger className="w-full h-9 bg-card">
                  <SelectValue placeholder="Date" />
                </SelectTrigger>
                <SelectContent>
                  {dateOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Input
                type="text"
                inputMode="numeric"
                pattern="[1-9][0-9]*"
                placeholder="Page Number"
                value={pageNumber}
                onChange={handlePageNumberChange}
                className="w-full h-9 bg-card selection:bg-transparent autofill:bg-card"
              />
            </div>
          </div>
          <Button
            variant="ghost"
            className="justify-start gap-2 px-0 text-sm font-medium text-foreground hover:bg-transparent"
          >
            <BookOpen className="h-4 w-4" />
            View all
          </Button>
          <div className="grid grid-cols-1 gap-4 py-2 w-fit">
            {[...Array(10)].map((_, i) => (
              <Badge
                key={i}
                className="w-[var(--panel-dropdown-width)] h-[var(--panel-single-grid-height)] rounded-lg border border-border bg-background hover:bg-background p-0 shadow-none"
              ></Badge>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}
